import * as React from "react";

import { cn } from "lib/utils";

type ToggleGroupType = "single";

type ToggleGroupContextValue = {
  type: ToggleGroupType;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
};

const ToggleGroupContext = React.createContext<ToggleGroupContextValue | null>(null);

type ToggleGroupProps = React.HTMLAttributes<HTMLDivElement> & {
  type?: ToggleGroupType;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
};

const ToggleGroup = React.forwardRef<HTMLDivElement, ToggleGroupProps>(
  ({ className, type = "single", value, onValueChange, disabled, children, ...props }, ref) => (
    <ToggleGroupContext.Provider value={{ type, value, onValueChange, disabled }}>
      <div
        ref={ref}
        role="group"
        className={cn("inline-flex items-center gap-2 rounded-xl border border-input bg-background p-1", className)}
        {...props}
      >
        {children}
      </div>
    </ToggleGroupContext.Provider>
  ),
);

ToggleGroup.displayName = "ToggleGroup";

type ToggleGroupItemProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  value: string;
};

const ToggleGroupItem = React.forwardRef<HTMLButtonElement, ToggleGroupItemProps>(
  ({ className, value, disabled, onClick, children, ...props }, ref) => {
    const context = React.useContext(ToggleGroupContext);
    const selected = context?.type === "single" ? context.value === value : false;
    const itemDisabled = Boolean(context?.disabled || disabled);

    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={itemDisabled}
        className={cn(
          "rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          selected
            ? "bg-foundation-base-darkbase-dark-500 text-white"
            : "bg-transparent text-foreground hover:bg-muted",
          itemDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
          className,
        )}
        onClick={(event) => {
          if (!itemDisabled) {
            context?.onValueChange?.(value);
          }
          onClick?.(event);
        }}
        {...props}
      >
        {children}
      </button>
    );
  },
);

ToggleGroupItem.displayName = "ToggleGroupItem";

export { ToggleGroup, ToggleGroupItem };
